import { formatBidDefault } from '../problems/BidCell';
import { getGenericBidComment } from '../problems/AuctionTable';

/**
 * The call rules that are true of the conceptual problem set and of no other (feature 029).
 *
 * <p>Kept separate from the component so they can be unit-tested as plain functions, and so it is
 * obvious how little is special: a handful of rules, all delegating everything they do not
 * recognise back to the shared defaults.</p>
 */

/** Any run of question marks: the data writes the decision point as `?` and, five times, `??`. */
const DECISION_MARKER = /^\?+$/;

/**
 * The wildcard: a call whose identity does not matter to the point being taught. The authoring
 * schema requires an annotation wherever it appears, and that annotation is what the reader is
 * actually meant to see (FR-017a).
 */
const WILDCARD = '*';

/**
 * A single option of a choice, once it has been derived. Mirrors the C# `BidTokens.Parse`
 * classification used by `CallAlternatives.Split`, so the two languages agree about what counts as
 * a choice at all — the display half of the same rule the legality half applies (FR-017c, FR-041).
 *
 * <p>Written out rather than delegating to `parseBid`, which logs a `console.warn` for every token
 * it cannot read: asking it about the halves of "yes/no" would make ordinary prose noisy.</p>
 */
const DERIVED_OPTION = /^(?:[1-7](?:nt|[cdhsmxy])|p|pass|-|x|dbl|double|xx|rdbl|redouble)$/i;

/** A denomination on its own: c, d, h, s, nt, or a generic letter (M, m, x, y) — the same set the C# `CallAlternatives` reads. */
const BARE_DENOMINATION = /^(?:[cdhsmxy]|nt)$/i;

/** The level of a plain bid — the "2" of "2c" — and nothing of anything else. */
const LEADING_LEVEL = /^[1-7](?=(?:[cdhsmxy]|nt)$)/i;

/** A level standing on its own: the "2" of "2/3M". */
const BARE_LEVEL = /^[1-7]$/;

/** A plain bid, capturing the denomination a bare level borrows from it. */
const PLAIN_BID = /^[1-7]((?:[cdhsmxy]|nt))$/i;

/** The suit glyphs, for the one place a choice has to be written as text rather than markup. */
const SUIT_GLYPH: Record<string, string> = {
  C: '♣',
  D: '♦',
  H: '♥',
  S: '♠',
};

/**
 * Expand a short-form choice into its full options (FR-017c).
 *
 * <p>Two inheritances, in this order:</p>
 * <ul>
 *   <li>An option naming only a DENOMINATION takes the level of the option before it —
 *       `2c/d` is `2c/2d`, `1d/M` is `1d/1M`.</li>
 *   <li>An option naming only a LEVEL takes the denomination of the option after it —
 *       `2/3M` is `2M/3M`.</li>
 * </ul>
 *
 * <p>Anything that is not a slash-separated set of calls comes back as ONE option, exactly as it
 * was written: `yes/no` is one prose answer and `5/5 in the majors` is one phrase, and neither may
 * be turned into a choice. That is the same guard, and the same reason, as the C# half in
 * `Libraries/BiddingLegality/CallAlternatives.cs` — whose `InheritLevel` this mirrors line for
 * line, and whose unit cases this function's tests reuse so the two cannot drift.</p>
 *
 * @returns every derived option, or a single-element array holding the original text.
 */
export function deriveChoice(call: string): string[] {
  const trimmed = call?.trim() ?? '';
  if (!trimmed.includes('/')) return [trimmed];

  const parts = trimmed
    .split('/')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  if (parts.length < 2) return [trimmed];

  const expanded = inheritLevel(parts);

  // One unreadable option and the whole thing is not a choice — see above.
  if (expanded.some((option) => !DERIVED_OPTION.test(option))) return [trimmed];

  return expanded;
}

function inheritLevel(parts: string[]): string[] {
  const expanded: string[] = [];
  let level: string | null = null;

  for (const original of parts) {
    let part = original;
    if (level !== null && BARE_DENOMINATION.test(part)) part = level + part;

    const bid = part.match(LEADING_LEVEL);
    level = bid ? bid[0] : null;
    expanded.push(part);
  }

  // Backward pass for "2/3M". Run second, over the already-expanded options, so a forward
  // inheritance is never disturbed by it.
  for (let i = expanded.length - 2; i >= 0; i--) {
    const option = expanded[i];
    if (!option || !BARE_LEVEL.test(option)) continue;

    const next = expanded[i + 1]?.match(PLAIN_BID);
    if (next?.[1]) expanded[i] = option + next[1];
  }

  return expanded;
}

/** True when this call offers three or more options and is therefore drawn as `*` (FR-017b). */
export function isManyWayChoice(call: string): boolean {
  return deriveChoice(call).length >= 3;
}

/**
 * One call written as text, with suit glyphs — `2c` as `2♣`, `1M` as `1M`.
 *
 * <p>Text, not markup, because it is bound for the tooltip, and the tooltip's channel is
 * `AuctionBid.comment`, a plain string that the shared `BidCell` renders with
 * `whitespace-pre-line`. Widening that channel to a `ReactNode` would mean editing `BidCell` and
 * `AuctionTable` for one caller's benefit, which is the thing this feature is built not to do
 * (FR-017d) — so the glyphs go in as characters and the colouring the cells give a suit is not
 * available here. The cells themselves are unaffected: they still draw every option through the
 * shared `formatBidDefault`.</p>
 */
function optionAsText(option: string): string {
  const bid = option.match(/^([1-7])(nt|[cdhs])$/i);
  if (bid?.[1] && bid[2]) {
    const suit = bid[2].toUpperCase();
    return `${bid[1]}${SUIT_GLYPH[suit] ?? suit}`;
  }

  if (/^(?:p|pass|-)$/i.test(option)) return 'Pass';
  if (/^(?:x|dbl|double)$/i.test(option)) return 'X';
  if (/^(?:xx|rdbl|redouble)$/i.test(option)) return 'XX';

  // A generic denomination (`1M`, `3m`, `2x`) stays as its literal letters, which is what
  // FR-017 asks the cells to draw too.
  return option;
}

/**
 * The whole choice, written out for a tooltip: `1♣ / 1♦ / 2♥ / 2♠`.
 *
 * <p>Only ever needed for a choice of three or more, which the cell draws as `*` — the reader has
 * to be able to get at what the `*` stands for, and the tooltip is where it goes (FR-017b).</p>
 */
export function choiceAsText(call: string): string {
  return deriveChoice(call).map(optionAsText).join(' / ');
}

/**
 * The annotation shown on a generic-denomination call (FR-017e).
 *
 * <p>Since 2026-09-11 this is the shared rule and nothing more. Until then it existed to add one
 * thing the shared default lacked — case sensitivity on `m`, which the shared rule read as a major —
 * and it deliberately left `x` alone as "Minor (C/D)", the reading the site had carried since
 * feature 005, for want of evidence to overturn it. The client has since settled all four letters
 * (`M` a major, `m` a minor, `x` any suit, `y` any suit above `x`), and because that is a fact
 * about the notation rather than a preference of this page, the correction went into the shared
 * `getGenericBidComment` itself, where the four practice-page calls that use these letters get it
 * too. This function stays as the seam the `bidCommentResolver` prop is wired through, so the
 * conceptual display can diverge again if it ever has to.</p>
 */
export function conceptualBidComment(call: string): string | null {
  return getGenericBidComment(call);
}

/**
 * Renders the calls the shared formatter does not recognise, and declines everything else so the
 * shared formatter draws it.
 *
 * <p>Four shapes are handled:</p>
 * <ul>
 *   <li><b>The wildcard</b> (`*`) — drawn deliberately as `*` rather than reaching the shared
 *       formatter's fall-through by accident. Its annotation, which the authoring schema requires,
 *       is what makes the cell yellow and gives it a tooltip, through exactly the channel every
 *       alerted bid on the site already uses (FR-017a).</li>
 *   <li><b>A choice of exactly two</b> (`4c/4d`, and now the short forms `2c/d`, `2/3M`) — both
 *       halves in the one cell, each through the shared suit markup, separated by a thin slash.
 *       Without this they render as raw text with no suit symbols at all, which is the one thing
 *       the constitution's Suit Symbol Display standard does not permit.</li>
 *   <li><b>A choice of three or more</b> (`1c/1d/2h/2s`) — drawn as `*`, because four calls do not
 *       fit an auction cell at any viewport this site supports. The whole choice goes to the
 *       tooltip instead, assembled by `ConceptualAuctionTable` (FR-017b).</li>
 *   <li><b>The decision marker</b> — `??` is drawn as the single `?` every other page draws. The
 *       server already normalises it; this is the belt to that braces, so a cell can never show a
 *       stray `??` if the data reaches the page by another route.</li>
 * </ul>
 *
 * <p>Generic-denomination calls (`1M`, `2m`, `3x`) are NOT handled here on purpose: the shared
 * formatter already falls through to the literal text, which is exactly what FR-017 asks for.</p>
 */
export function conceptualCallFormatter(call: string): JSX.Element | null {
  if (!call) return null;

  if (DECISION_MARKER.test(call)) {
    return <span className="text-black">?</span>;
  }

  if (call.trim() === WILDCARD) {
    return <span className="text-black font-semibold">{WILDCARD}</span>;
  }

  const options = deriveChoice(call);

  if (options.length >= 3) {
    return (
      <span className="text-black font-semibold" aria-label={`one of ${choiceAsText(call)}`}>
        {WILDCARD}
      </span>
    );
  }

  if (options.length === 2) {
    const [first, second] = options;
    return (
      <span className="whitespace-nowrap">
        {formatBidDefault(first!)}
        <span className="text-ink-soft mx-0.5" aria-hidden="true">
          /
        </span>
        <span className="sr-only"> or </span>
        {formatBidDefault(second!)}
      </span>
    );
  }

  return null;
}
